/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import AppContext from "AppContext";
import { useDispatch, useSelector } from "react-redux";
import LoadingPage from "components/common_components/LoadingPage";
import Page from "components/common_components/Page/Page";
import { notifyError } from "redux/actions/Notifications";
import { Form as FormikForm, AutocompleteField, DateTimePickerField } from "components/common_components/Formik";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ArrowRightIcon from "@material-ui/icons/ArrowRight";
import { taskStatuses } from "utils/static/workflowsStaticData";
import Task from "./Task";
import Chart from "react-google-charts";
import AddTaskDialog from "./AddTaskDialog";
import EditTaskDialog from "./EditTaskDialog";
import { hasElements } from "utils/arrays";
import TeamWork from "./Reports/TeamWork";
import ButtonNew from "components/common_components/Button";
import Icon from "components/common_components/Icon";
import { getDateDay, getDateHour, getDateMinute, getDateMonth, getDateYear } from "utils/dateUtils";

const statusTitleStyles = taskStatuses.reduce((map, item) => {
    return {
        ...map,
        [item.value]: {
            padding: 5,
            justifyContent: "flex-start",
            display: "flex",
            alignItems: "center",
            borderRadius: 4,
            ...item.style,
        },
        [`${[item.value]}_icon`]: {
            display: "flex",
            alignItems: "center",
            marginRight: 5,
        },
    };
}, {});

const statusOptionStyles = taskStatuses.reduce((map, item) => {
    return {
        ...map,
        [`status_option_${item.value}`]: {
            padding: 5,
            display: "flex",
            justifyContent: "flex-start",
            ...item.style,
            backgroundColor: "transparent",
            width: "100%",
        },
        [`status_option_${item.value}_icon`]: {
            display: "flex",
            alignItems: "center",
            marginRight: 5,
        },
    };
}, {});

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        flex: 1,
        flexDirection: "column",
        width: "100%",
    },
    title: {
        fontSize: "large",
    },
    filtersContainer: {
        margin: 20,
        padding: 20,
        border: "#dcdcdc 1px dashed",
        borderRadius: "5px",
        borderBottomColor: "#dcdcdc",
    },
    filtersButtonsContainer: {
        display: "flex",
        alignItems: "flex-end",
    },
    board: {
        padding: 20,
    },
    statusesContainer: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
    },
    assigneesContainer: {
        display: "flex",
        flexDirection: "column",
    },
    noResults: {
        display: "flex",
        justifyContent: "center",
        marginTop: 100,
        marginBottom: 100,
    },
    assigneeContainer: {
        display: "flex",
        flexDirection: "column",
    },
    assigneeContainerTitle: {
        paddingTop: 5,
        paddingBottom: 5,
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        marginTop: 5,
        backgroundColor: "#fbfbfb",
        "-webkit-transition": "all 0.3s ease",
        "-moz-transition": "all 0.3s ease",
        transition: "all 0.3s ease",
        "&:hover": {
            backgroundColor: () => "#f1f1f1",
        },
    },
    assigneeStatusesContainer: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        marginTop: 5,
    },
    assigneeStatusContainer: {
        flexBasis: `${100 / taskStatuses.length - 2}%`,
    },
    ...statusTitleStyles,
    assigneeStatusTasksContainer: {
        display: "flex",
        flexDirection: "column",
        padding: 10,
        backgroundColor: "#f6f7fe8a",
        height: "100%",
        minHeight: 100,
        border: "2px #3f51b521 dashed",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "0px 20px",
        fontSize: "large",
        marginBottom: 20,
        marginTop: 30,
    },
    actions: {
        display: "flex",
        justifyContent: "flex-end",
        gap: 10,
    },
    autocompleteOption: {
        display: "flex",
        flexDirection: "column",
        padding: 5,
        width: "100%",
    },
    autocompleteOptionUserName: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
    },
    adminFlag: {
        fontSize: "smaller",
        color: "silver",
    },
    ...statusOptionStyles,
}));

function Dashboard({ taskIdToPreload }) {
    const classes = useStyles();
    const [preloadTaskId, setPreloadTaskId] = useState(taskIdToPreload);
    const [resources, setResources] = useState({ users: [], modules: [], types: [] });
    const [tasksByAssigneeMap, setTasksByAssigneeMap] = useState(undefined);
    const [searchPerformed, setSearchPerformed] = useState(false);
    const dispatch = useDispatch();
    const [isLoadingFilters, setIsLoadingFilters] = useState(false);
    const [isLoadingTasks, setIsLoadingTasks] = useState(false);
    const [expandedAssignees, setExpandedAssignees] = useState([]);
    const { user } = useSelector((state) => state.Login);
    const [tasksResponse, setTasksResponse] = useState(undefined);
    const [activeFilters, setActiveFilters] = useState({
        assigneeUser: null,
        ownerUser: null,
        status: null,
        module: null,
        type: null,
        dueDateFrom: null,
        dueDateTo: null,
    });
    const [reportEnabled, setReportEnabled] = useState(false);
    const [addTaskFormDialogProps, setAddTaskFormDialogProps] = useState({
        open: false,
        maxWidth: "md",
        fullWidth: true,
        title: "",
        formId: `in-dialog-form-new-task`,
        onClose: () => {
            setAddTaskFormDialogProps((prev) => ({ ...prev, open: false }));
        },
    });

    const {
        api: {
            Settings: { Users, Modules, Types },
            Workflows: { Tasks },
        },
    } = useContext(AppContext);

    function getUsers() {
        setIsLoadingFilters(true);
        Users.getAll({
            onSuccess: (data) => {
                setResources((prev) => ({
                    ...prev,
                    users: data,
                }));
                setIsLoadingFilters(false);
            },
            onError: (error) => {
                setResources((prev) => ({
                    ...prev,
                    users: [],
                }));
                dispatch(notifyError(error));
                setIsLoadingFilters(false);
            },
        });
    }

    function getModules() {
        setIsLoadingFilters(true);
        Modules.getAll({
            onSuccess: (data) => {
                setResources((prev) => ({
                    ...prev,
                    modules: data,
                }));
                setIsLoadingFilters(false);
            },
            onError: (error) => {
                setResources((prev) => ({
                    ...prev,
                    modules: [],
                }));
                dispatch(notifyError(error));
                setIsLoadingFilters(false);
            },
        });
    }

    function getTypes() {
        setIsLoadingFilters(true);
        Types.getAll({
            onSuccess: (data) => {
                setResources((prev) => ({
                    ...prev,
                    types: data,
                }));
                setIsLoadingFilters(false);
            },
            onError: (error) => {
                setResources((prev) => ({
                    ...prev,
                    types: [],
                }));
                dispatch(notifyError(error));
                setIsLoadingFilters(false);
            },
        });
    }

    function processResponse(response) {
        if (hasElements(response)) {
            const arrAssigneeUsersDistinct = response.reduce(
                (arr, item) => (arr.includes(item.assigneeUserId) ? arr : [...arr, item.assigneeUserId]),
                []
            );
            if (!user.isAdmin) {
                setExpandedAssignees([String(arrAssigneeUsersDistinct[0])]);
            }
            const objTasksByUser = arrAssigneeUsersDistinct.reduce(
                (map, item) => ({ ...map, [item]: response.filter((x) => x.assigneeUserId === item) }),
                {}
            );
            return objTasksByUser;
        } else {
            return undefined;
        }
    }

    async function onSubmitHandler(values = {}) {
        const { assigneeUser, ownerUser, status, module, type, dueDateFrom, dueDateTo } = values;

        const payload = {
            taskId: preloadTaskId || null,
            assigneeUserId: user.isAdmin ? assigneeUser && assigneeUser.id : user.id,
            ownerUserId: ownerUser && ownerUser.id,
            status: status && status.value,
            moduleId: module && module.id,
            typeId: type && type.id,
            dueDateFrom: dueDateFrom &&
                dueDateFrom instanceof Date && {
                    year: getDateYear(dueDateFrom),
                    month: getDateMonth(dueDateFrom), // zero-based
                    day: getDateDay(dueDateFrom),
                    hour: getDateHour(dueDateFrom),
                    minute: getDateMinute(dueDateFrom),
                },
            dueDateTo: dueDateTo &&
                dueDateTo instanceof Date && {
                    year: getDateYear(dueDateTo),
                    month: getDateMonth(dueDateTo), // zero-based
                    day: getDateDay(dueDateTo),
                    hour: getDateHour(dueDateTo),
                    minute: getDateMinute(dueDateTo),
                },
        };

        try {
            setReportEnabled(false);
            setSearchPerformed(true);
            setIsLoadingTasks(true);
            const response = await Tasks.getAllAsync({ payload });
            setTasksResponse(response);
            setTasksByAssigneeMap(processResponse(response));
        } catch (error) {
            setTasksResponse(undefined);
            setTasksByAssigneeMap(undefined);
            dispatch(notifyError(error));
        } finally {
            setIsLoadingTasks(false);
        }
    }

    useEffect(() => {
        getUsers();
        getModules();
        getTypes();

        if (!user.isAdmin) {
            onSubmitHandler();
        }

        return () => {
            setTasksResponse(undefined);
            setTasksByAssigneeMap(undefined);
        };
    }, []);

    useEffect(() => {
        if (preloadTaskId) {
            onSubmitHandler();
        }

        return () => {
            setPreloadTaskId(undefined);
        };
    }, [preloadTaskId]);

    function onExpandAssigneeHandler(assigneeUserId) {
        setExpandedAssignees([...expandedAssignees, assigneeUserId]);
    }

    function onCollapseAssigneeHandler(assigneeUserId) {
        setExpandedAssignees(expandedAssignees.filter((x) => String(x) !== String(assigneeUserId)));
    }

    function isAssigneeExpanded(assigneeUserId) {
        return expandedAssignees.some((x) => String(x) === String(assigneeUserId));
    }

    function onFilterFieldChangeHandler({ fieldName, value }) {
        setActiveFilters((prev) => ({ ...prev, [fieldName]: value }));
    }

    function generatePieChartData() {
        const data = [["Status", "Tasks per status"]];
        if (hasElements(tasksResponse)) {
            return taskStatuses.reduce((arr, status) => {
                const tasksPerStatus = tasksResponse.filter((t) => t.status === status.value);
                return [...arr, [status.label, tasksResponse && tasksPerStatus.length]];
            }, data);
        }
        return data;
    }

    function generatePieChartColors() {
        return taskStatuses.reduce((map, status, index) => {
            return {
                ...map,
                [index]: {
                    color: status.style.backgroundColor,
                    textStyle: { color: status.style.color },
                    offset: status.value === "closed" ? 0.05 : 0,
                },
            };
        }, {});
    }

    function onAddTaskClickHandler() {
        setAddTaskFormDialogProps((prev) => ({ ...prev, open: true }));
    }

    function onViewReportClickHandler() {
        setReportEnabled(!reportEnabled);
    }

    function AssigneeTitle({ assigneeUserId, assigneeUserTasks }) {
        const expanded = isAssigneeExpanded(assigneeUserId);

        const ArrowIconComponent = expanded ? ArrowDropDownIcon : ArrowRightIcon;

        const onToggleHandler = () => (expanded ? onCollapseAssigneeHandler(assigneeUserId) : onExpandAssigneeHandler(assigneeUserId));

        return (
            <div className={classes.assigneeContainerTitle} onClick={onToggleHandler}>
                <ArrowIconComponent />
                {`${assigneeUserId === "null" ? "Unassigned" : assigneeUserTasks[0].assigneeUserFullName} (${
                    assigneeUserTasks.length
                } tasks)`}
            </div>
        );
    }

    return (
        <Page className={classes.root}>
            <Paper className={classes.paper}>
                <div className={classes.header}>
                    <div className={classes.title}>{user.isAdmin ? "Team's work" : "Your work"}</div>
                    <div className={classes.actions}>
                        {reportEnabled && (
                            <ButtonNew secondary outline onClick={onViewReportClickHandler} small>
                                <Icon name="arrow" size="xs" color="secondary" />
                                <span>Back</span>
                            </ButtonNew>
                        )}
                        {!reportEnabled && (
                            <>
                                <ButtonNew secondary outline onClick={onAddTaskClickHandler} small>
                                    <Icon name="workflow" size="xs" color="secondary" />
                                    <span>Create task</span>
                                </ButtonNew>
                                <ButtonNew
                                    secondary
                                    outline
                                    onClick={onViewReportClickHandler}
                                    small
                                    disabled={!hasElements(tasksResponse)}
                                >
                                    <Icon name="reports" size="xs" color="secondary" />
                                    <span>View report</span>
                                </ButtonNew>
                            </>
                        )}
                    </div>
                </div>
                {!reportEnabled && (
                    <>
                        <div className={classes.filtersContainer}>
                            {isLoadingFilters ? (
                                <LoadingPage />
                            ) : (
                                <FormikForm
                                    validateOnChange
                                    initialValues={{
                                        assigneeUser: null,
                                        ownerUser: null,
                                        status: null,
                                        module: null,
                                        type: null,
                                        dueDateFrom: null,
                                        dueDateTo: null,
                                    }}
                                    validationSchema={undefined}
                                    onSubmit={onSubmitHandler}
                                    showButtons={false}
                                >
                                    {({ values, setFieldValue, isSubmitting }) => (
                                        <Grid container spacing={1}>
                                            {user.isAdmin && (
                                                <>
                                                    <Grid item xs={12} sm={4} md={3}>
                                                        <AutocompleteField
                                                            fieldName="assigneeUser"
                                                            label="Assignee"
                                                            variant="standard"
                                                            options={resources.users}
                                                            onChange={onFilterFieldChangeHandler}
                                                            valueKey="id"
                                                            labelKey="name"
                                                            renderOption={(option) => (
                                                                <div className={classes.autocompleteOption}>
                                                                    <div className={classes.autocompleteOptionUserName}>
                                                                        <div>
                                                                            <strong>{option.name}</strong>
                                                                        </div>
                                                                        <small className={classes.adminFlag}>
                                                                            {option.isAdmin ? "admin" : ""}
                                                                        </small>
                                                                    </div>
                                                                    <small>{option.email}</small>
                                                                </div>
                                                            )}
                                                            getOptionLabel={(option) => {
                                                                if (option) {
                                                                    return option.name;
                                                                }
                                                                return "";
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={4} md={3}>
                                                        <AutocompleteField
                                                            fieldName="ownerUser"
                                                            label="Workflow Owner"
                                                            variant="standard"
                                                            options={resources.users}
                                                            onChange={onFilterFieldChangeHandler}
                                                            valueKey="id"
                                                            labelKey="name"
                                                            renderOption={(option) => (
                                                                <div className={classes.autocompleteOption}>
                                                                    <div className={classes.autocompleteOptionUserName}>
                                                                        <div>
                                                                            <strong>{option.name}</strong>
                                                                        </div>
                                                                        <small className={classes.adminFlag}>
                                                                            {option.isAdmin ? "admin" : ""}
                                                                        </small>
                                                                    </div>
                                                                    <small>{option.email}</small>
                                                                </div>
                                                            )}
                                                            getOptionLabel={(option) => {
                                                                if (option) {
                                                                    return option.name;
                                                                }
                                                                return "";
                                                            }}
                                                        />
                                                    </Grid>
                                                </>
                                            )}
                                            <Grid item xs={12} sm={4} md={3}>
                                                <AutocompleteField
                                                    fieldName="status"
                                                    label="Status"
                                                    variant="standard"
                                                    options={taskStatuses}
                                                    onChange={onFilterFieldChangeHandler}
                                                    valueKey="value"
                                                    labelKey="label"
                                                    renderOption={(option) => (
                                                        <div className={classes[`status_option_${option.value}`]}>
                                                            <div className={classes[`status_option_${option.value}_icon`]}>
                                                                {option.icon}
                                                            </div>
                                                            <div>{option.label}</div>
                                                        </div>
                                                    )}
                                                    getOptionLabel={(option) => {
                                                        if (option) {
                                                            return option.label;
                                                        }
                                                        return "";
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4} md={3}>
                                                <AutocompleteField
                                                    fieldName="module"
                                                    label="Module"
                                                    variant="standard"
                                                    options={resources.modules}
                                                    onChange={(params) => {
                                                        onFilterFieldChangeHandler(params);
                                                        setFieldValue("type", null);
                                                    }}
                                                    valueKey="id"
                                                    labelKey="name"
                                                    renderOption={(option) => <div>{option && option.name}</div>}
                                                    getOptionLabel={(option) => (option ? option.name : "")}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4} md={3}>
                                                <AutocompleteField
                                                    fieldName="type"
                                                    label="Type"
                                                    variant="standard"
                                                    options={resources.types.filter(
                                                        (x) => x.module.id === (values.module && values.module.id)
                                                    )}
                                                    onChange={onFilterFieldChangeHandler}
                                                    valueKey="id"
                                                    labelKey="type"
                                                    renderOption={(option) => <div>{option && option.type}</div>}
                                                    getOptionLabel={(option) => (option ? option.type : "")}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4} md={3}>
                                                <DateTimePickerField
                                                    fieldName="dueDateFrom"
                                                    label="Due Date From"
                                                    inputVariant="standard"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4} md={3}>
                                                <DateTimePickerField fieldName="dueDateTo" label="Due Date To" inputVariant="standard" />
                                            </Grid>
                                            <Grid item xs={12} sm={4} md={3} className={classes.filtersButtonsContainer}>
                                                <ButtonNew secondary small type="submit" loading={isSubmitting}>
                                                    {!isSubmitting && <Icon name="search" size="xs" color="white" />}
                                                    <span>{isSubmitting ? "Searching" : "Search"}</span>
                                                </ButtonNew>
                                            </Grid>
                                        </Grid>
                                    )}
                                </FormikForm>
                            )}
                        </div>
                        <div className={classes.board}>
                            <div className={classes.statusesContainer}>
                                {taskStatuses.map((s) => (
                                    <div key={`status_header_${s.value}`} className={classes.assigneeStatusContainer}>
                                        <div className={classes[s.value]}>
                                            <div className={classes[`${s.value}_icon`]}>{s.icon}</div>{" "}
                                            <div className={classes[`${s.value}_label`]}>{s.label}</div>{" "}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {isLoadingTasks ? (
                                <LoadingPage />
                            ) : (
                                <>
                                    {tasksByAssigneeMap && typeof tasksByAssigneeMap === "object" ? (
                                        <div className={classes.assigneesContainer}>
                                            {Object.keys(tasksByAssigneeMap).map((assigneeUserId) => {
                                                const assigneeUserTasks = tasksByAssigneeMap[assigneeUserId];
                                                return (
                                                    <div key={`assignee_${assigneeUserId}`} className={classes.assigneeContainer}>
                                                        <AssigneeTitle
                                                            assigneeUserId={assigneeUserId}
                                                            assigneeUserTasks={assigneeUserTasks}
                                                        />
                                                        {isAssigneeExpanded(assigneeUserId) && (
                                                            <Grid container className={classes.assigneeStatusesContainer}>
                                                                {hasElements(assigneeUserTasks) &&
                                                                    taskStatuses.map((s) => {
                                                                        const tasksByStatus = assigneeUserTasks.filter(
                                                                            (x) => x.status === s.value
                                                                        );
                                                                        return (
                                                                            <Grid
                                                                                item
                                                                                key={`assignee_${assigneeUserId}_status_${s.value}`}
                                                                                className={classes.assigneeStatusContainer}
                                                                            >
                                                                                <div className={classes.assigneeStatusTasksContainer}>
                                                                                    {hasElements(tasksByStatus) &&
                                                                                        tasksByStatus.map((t) => (
                                                                                            <Task
                                                                                                key={`assignee_${assigneeUserId}_status_${s.value}_task_${t.id}`}
                                                                                                task={t}
                                                                                                resources={resources}
                                                                                                reloadTasks={() =>
                                                                                                    onSubmitHandler(activeFilters)
                                                                                                }
                                                                                            />
                                                                                        ))}
                                                                                </div>
                                                                            </Grid>
                                                                        );
                                                                    })}
                                                            </Grid>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                            <div>
                                                <Chart
                                                    chartType="PieChart"
                                                    loader={<div className="loading" />}
                                                    data={generatePieChartData()}
                                                    options={{
                                                        is3D: false,
                                                        legend: "none",
                                                        pieSliceText: "label",
                                                        slices: generatePieChartColors(),
                                                        pieSliceBorderColor: "gray",
                                                        height: 500,
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className={classes.noResults}>
                                            {searchPerformed ? "No results found!" : "Click on the Search button!"}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </>
                )}

                {reportEnabled && (
                    <Grid container className={classes.board}>
                        <Grid item xs={12}>
                            <TeamWork data={tasksResponse} currentUser={user} />
                        </Grid>
                    </Grid>
                )}
            </Paper>
            <AddTaskDialog resources={resources} onSuccessCallback={() => onSubmitHandler(activeFilters)} {...addTaskFormDialogProps} />
            <EditTaskDialog
                open={preloadTaskId && hasElements(tasksResponse) && String(tasksResponse[0].id) === String(preloadTaskId)}
                task={hasElements(tasksResponse) && tasksResponse[0]}
                resources={resources}
                onSuccessCallback={() => onSubmitHandler(activeFilters)}
                maxWidth="lg"
                fullWidth
                title=""
                formId={`in-dialog-form-task_edit`}
                onClose={() => {
                    setPreloadTaskId(undefined);
                }}
            />
        </Page>
    );
}

export default Dashboard;
